import React, {useEffect, useMemo, useState} from 'react';
import DocumentTitle from 'react-document-title';
import {useNavigate, useParams} from 'react-router-dom';

import {useUserEmail} from 'src/hooks/useUserEmail';
import {getUser} from 'src/modules/admin/services';
import {NOT_EDIT_CHANGE_REQUEST_STATUSES, OPENED_CHANGE_REQUEST_STATUSES} from 'src/modules/constants';
import {sortAttributes} from 'src/modules/event/utils/sortAttributes';
import {sortChangeRequests} from 'src/modules/event/utils/sortChangeRequests';
import {EventCreationMode} from 'src/modules/event-create/constants';
import {createEventCreationRoutePath} from 'src/modules/event-create/event-creation-route-planner';
import {createEventReviewRoutePath} from 'src/modules/event-review/event-review-route-planner';
import {UsersInfoController} from 'src/modules/users-info/users-info.controller';
import {
    getChangeRequestByCodeAndSourceAndType,
    getChangeRequestByCodeAndSourceAndTypeSuccess,
} from 'src/openapi/apis/ChangeRequestControllerApi';
import {getEventById} from 'src/openapi/apis/EventControllerApi';
import {getCurrentUser} from 'src/openapi/apis/UserControllerApi';
import {ChangeRequest} from 'src/openapi/models/ChangeRequest';
import {useAppDispatch, useAppSelector} from 'src/store';

import {Loading} from '@pb/kit-component-loading';

import {EventView} from './event.view';
import {getEventInfo} from './services';

export const EventController = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const userEmail = useUserEmail();
    const user = useAppSelector(getUser);
    const [isLoading, setIsLoading] = useState(true);
    const [openChangeRequest, setOpenChangeRequest] = useState(false);
    const eventId = parseInt(useParams<{eventId: string}>()?.eventId || '');
    const eventCodeInfo = useAppSelector(getEventInfo)?.[eventId];
    const [changeRequests, setChangeRequests] = useState<Array<ChangeRequest>>([]);
    const [openedChangeRequest, setOpenedChangeRequest] = useState<ChangeRequest>();

    const authorsEmails = useMemo(() => [
        ...new Set(changeRequests.map(changeRequest => changeRequest.authorEmail)),
    ], [changeRequests]);

    useEffect(() => {
        if (eventId) {
            dispatch(getEventById({id: +eventId}));
        }
    }, [dispatch, eventId]);

    useEffect(() => {
        if (eventCodeInfo) {
            const eventCode = eventCodeInfo.code;
            const eventSource = eventCodeInfo.source;
            const eventType = eventCodeInfo.type;
            dispatch(getChangeRequestByCodeAndSourceAndType({eventCode, eventSource, eventType}))
                .then(res => {
                    if (!res.payload.error && res.type === getChangeRequestByCodeAndSourceAndTypeSuccess.type) {
                        const changeRequestsByCodeAndSource = res.payload.data;
                        const existingOpenedChangeRequest = changeRequestsByCodeAndSource
                            .find(changeRequest => OPENED_CHANGE_REQUEST_STATUSES.includes(changeRequest.status));

                        if (existingOpenedChangeRequest) {
                            const isUserChangeRequestAuthor = existingOpenedChangeRequest.authorEmail === userEmail;
                            const openExistingChangeRequest = !isUserChangeRequestAuthor || isUserChangeRequestAuthor &&
                                NOT_EDIT_CHANGE_REQUEST_STATUSES.includes(existingOpenedChangeRequest.status);

                            setOpenChangeRequest(openExistingChangeRequest);
                        }

                        setChangeRequests(sortChangeRequests(changeRequestsByCodeAndSource));
                        setOpenedChangeRequest(existingOpenedChangeRequest);
                        setIsLoading(false);
                    }
                });
        }
    }, [dispatch, eventCodeInfo, userEmail]);

    useEffect(() => {
        if (!user) {
            dispatch(getCurrentUser());
        }
    }, [dispatch, user]);

    const updateHandler = () => {
        const eventCreationRoutePath = createEventCreationRoutePath({
            mode: EventCreationMode.update,
            eventId: eventCodeInfo.id,
            changeRequestId: openedChangeRequest?.id ?? 0,
        });

        navigate(eventCreationRoutePath);
    };

    const seeChangeRequestHandler = () => {
        if (openedChangeRequest?.id) {
            const eventReviewRoutePath = createEventReviewRoutePath({changeRequestId: openedChangeRequest.id});
            window.open(eventReviewRoutePath);
        }
    };

    return eventCodeInfo && !isLoading ? (
        <>
            <DocumentTitle title={`${eventCodeInfo.code} - Event`}/>
            <UsersInfoController emails={authorsEmails}/>
            <EventView
                changeRequests={changeRequests}
                eventCode={eventCodeInfo.code}
                eventDetailInfo={sortAttributes(eventCodeInfo)}
                updateHandler={updateHandler}
                seeChangeRequestHandler={seeChangeRequestHandler}
                openChangeRequest={openChangeRequest}
                user={user}
            />
        </>
    ) : <Loading fast={true}/>;
};
